import React, { useState } from "react";
import { History, Location } from "history";
import { compose } from "redux";
import { useDispatch, useSelector, Dispatch } from "react-redux";
import { useEffectOnce, usePrevious, useSearch } from "hooks";
import { withLastLocation } from "react-router-last-location";
import MainLayout from "components/MainLayout";
import TogglesHandler from "containers/TogglesHandler";
import TrackCurrentRow, {
    type TrackCurrentRowProps,
} from "containers/TrackCurrentRow";
import {
    type ToggleDTO,
    SearchScope,
    type ArrowNavigationSetter,
} from "data/types";
import SplitPanes from "containers/SplitPanes";
import MainSearch from "components/MainSearch";
import SearchForm from "containers/SearchForm";
import SearchResultContainer from "containers/SearchResult";
import { type ConstantsEnum } from "data/constants";
import loc from "i18next";
import * as s from "data/reducers/selectors";
import styles from "./index.module.css";
import Pane from "components/SplitPanes/Pane";
import withArrowNavigation from "containers/ArrowNavigation";
import withMultiSelection, {
    type MultiSelectionProps,
} from "containers/MultiSelection";
import withPaginationHandler, {
    useSearchPagination,
    type PaginationHandlerProps,
} from "containers/PaginationHandler";
import { getGlobalSearchDefaultAction } from "data/actions";
import CloseButton from "@hs/close-button";

export const viewName = "minisearch";

type Props = {
    /** react-router history */
    history: History,
    /** react-router location */
    location: Location,
    /** react-router-last-location */
    lastLocation: ?Location,
    /** TrackCurrentRow: current selected search form row */
    sf: TrackCurrentRowProps,
    /** fetch status while getting search form infos */
    status: ConstantsEnum,
    /** which toggles are currently selected */
    selectedToggles: Array<string>,
    /** TogglesHandler callback to set toggles */
    setToggles: Function,
    /** TogglesHandler: current toggles */
    toggles: ?ToggleDTO,
    /** SplitPanes callback to set split sizes */
    setSplitSizes: Function,
    /** TrackCurrentRow: current selected document row */
    sr: TrackCurrentRowProps,
    /** ArrowNavigation Setter Functions */
    postMessage: ?ArrowNavigationSetter,
    /** withMultipleSelection additional props */
    multiSelection: MultiSelectionProps,
    /** PaginationHandler additional props */
    pagination: PaginationHandlerProps,
    /** optional callback to handle click */
    onSearchResultRowClick: (
        e: SyntheticMouseEvent<*>,
        rowIndex: number,
        row: SearchResultRowDTO
    ) => void,
    /** optional overrides for split panes to work */
    paneIds?: Array<string>,
    /** optional disable any actions on mini search view */
    actionsDisabled?: boolean,
    /** optional callback (will render Close Button, when set) */
    onClose?: () => void,
    /** optional node to render before the top row */
    topRowBefore?: Node,
};

export const MiniSearchView = ({
    toggles,
    setToggles,
    sf: { currentRow, setCurrentRow },
    sr: { currentRow: srCurrentRow },
    postMessage,
    multiSelection,
    pagination,
    onSearchResultRowClick,
    paneIds = ["mainPanel", "searchPanel", "searchResult"],
    actionsDisabled,
    onClose,
    topRowBefore,
    filters,
    itemUri,
    searchScope = SearchScope.AllSites,
}: Props) => {
    const { setSource } = postMessage || {};

    //#region redux
    const dispatch: Dispatch = useDispatch();
    const searchResultStatus = useSelector(s.searchStatusSelector);
    const totalSearchResults = useSelector(s.searchResultTotalCountSelector);
    const searchForm = useSelector(s.searchFormResultSelector);
    const searchFormId = useSelector(s.searchFormIdSelector);
    //#endregion

    //#region state
    const [searchItem, setSearchItem] = useState(null);
    const [crumbSearchItem, setCrumbSearchItem] = useState(null);
    //#endregion

    useEffectOnce(() => {
        dispatch(getGlobalSearchDefaultAction());
    });

    const [handleSearch, clearSearchResults, handleSearchFormCancel] =
        useSearch({
            searchItem,
            setSearchItem,
            toggles,
            setToggles,
            setCurrentRow,
            viewName,
            searchFormId,
            searchForm,
            crumbSearchItem,
            setCrumbSearchItem,
            currentRow,
            setSource,
            searchResultStatus,
        });

    return (
        <MainLayout
            name={viewName}
            itemUri="search"
            toggles={toggles}
            breadcrumbReadonly={true}
            searchItem={crumbSearchItem}
            contextProps={
                actionsDisabled
                    ? null
                    : {
                          multiSelection,
                          searchItem,
                      }
            }
            actionsDisabled={
                searchItem == null ||
                totalSearchResults === 0 ||
                actionsDisabled
            }
            topRowBefore={topRowBefore}
            topRowEnd={onClose && <CloseButton onClick={onClose} />}
        >
            <div
                id={paneIds[0]}
                className={`${styles.wrapper} ${styles.split}`}
            >
                <Pane id={paneIds[1]} className={styles.form}>
                    {currentRow === -1 ? (
                        <MainSearch
                            searchText=""
                            searchScope={searchScope}
                            itemUri={itemUri || ""}
                            onSearch={handleSearch}
                            onClear={clearSearchResults}
                            dispatch={dispatch}
                            filters={filters}
                        />
                    ) : (
                        <SearchForm
                            id={currentRow}
                            searchLabel={loc.t("search:searchButton")}
                            searchEnabled={!!searchForm}
                            onSearch={setSearchItem}
                            onCancel={handleSearchFormCancel}
                        />
                    )}
                </Pane>
                <Pane id={paneIds[2]} className={styles.result}>
                    <SearchResultContainer
                        activeRowIndex={srCurrentRow}
                        searchItem={searchItem}
                        multiSelection={multiSelection}
                        pagination={pagination}
                        onRowClick={onSearchResultRowClick}
                    />
                </Pane>
            </div>
        </MainLayout>
    );
};

export default compose(
    withLastLocation,
    TogglesHandler({ viewName }),
    TrackCurrentRow({ namespace: "sf", searchParamName: "sfid" }),
    TrackCurrentRow({
        namespace: "sr",
        searchParamName: "sri",
    }),
    SplitPanes({
        viewName,
        paneIds: ["#searchPanel", "#searchResult"],
        toggleIds: ["search", "result"],
    }),
    withArrowNavigation({ qsParam: "sri" }),
    withMultiSelection({
        rowTrackerNamespace: "sr",
        delay: 600,
    }),
    withPaginationHandler({
        namespace: "sr",
        usePagination: useSearchPagination,
    })
)(MiniSearchView);
